import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { PauseCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { default as Tooltip } from 'antd/es/tooltip';
import cn from 'classnames';
import { CountryFlag } from 'shared/src/CountryFlag';

import { InfoCircleIcon } from '@atoms/Icon/InfoCircleIcon';
import { SharedAccountIcon } from '@atoms/Icon/SharedAccountIcon';

import type { AccountWidgetViewState } from './utils';

type AccountStatusBadgeProps = Pick<
  AccountWidgetViewState,
  'isSuspendedAccount' | 'isTerminatedAccount' | 'statusBadgeLabel'
>;

type AccountIconProps = Pick<
  AccountWidgetViewState,
  | 'countryCode'
  | 'isSharedAccount'
  | 'isSuspendedAccount'
  | 'isTerminatedAccount'
>;

type AccountOwnedContentProps = Pick<
  AccountWidgetViewState,
  'balanceValue' | 'currencySymbol' | 'isTerminatedAccount'
>;

export const AccountStatusBadge = ({
  isSuspendedAccount,
  isTerminatedAccount,
  statusBadgeLabel
}: AccountStatusBadgeProps) => {
  if (!statusBadgeLabel) {
    return null;
  }

  return (
    <div
      className={cn('h-fit rounded px-2 py-1 text-sm', {
        'bg-border-default text-content-primary': isTerminatedAccount,
        'bg-feedback-attention text-white': isSuspendedAccount
      })}
    >
      {statusBadgeLabel}
    </div>
  );
};

export const AccountIcon = ({
  countryCode,
  isSharedAccount,
  isSuspendedAccount,
  isTerminatedAccount
}: AccountIconProps) => {
  const overlayIcon = isTerminatedAccount ? (
    <XCircleIcon
      className="size-5 text-content-strong"
      aria-label="Terminated account"
      title="Terminated account"
    />
  ) : isSuspendedAccount ? (
    <PauseCircleIcon
      className="size-5 text-feedback-attention"
      aria-label="Suspended account"
      title="Suspended account"
    />
  ) : isSharedAccount ? (
    <SharedAccountIcon height="24" width="24" className="absolute inset-0" />
  ) : null;

  if (!overlayIcon) {
    return (
      <CountryFlag className="!size-8" countryCode={countryCode ?? ''} svg />
    );
  }

  return (
    <div className="relative size-8">
      <CountryFlag
        className={cn('absolute left-0 top-0 !size-6', {
          'opacity-40 grayscale': isTerminatedAccount
        })}
        countryCode={countryCode ?? ''}
        svg
      />
      <div className="absolute bottom-0 right-0 flex size-5 items-center justify-center rounded-full bg-white">
        {overlayIcon}
      </div>
    </div>
  );
};

export const AccountOwnedContent = ({
  balanceValue,
  currencySymbol,
  isTerminatedAccount
}: AccountOwnedContentProps) => {
  return (
    <>
      <div className="flex items-center justify-end gap-1 text-2xl font-medium">
        {currencySymbol && <span>{currencySymbol}</span>}
        <span>{balanceValue}</span>
      </div>
      {/* NOTE (HJST): div is needed by safari to enforce svg size */}
      <div>
        <ArrowLongRightIcon
          className={cn('stroke-content-subtle', {
            'group-hover:stroke-content-primary group-active:stroke-content-primary':
              !isTerminatedAccount
          })}
        />
      </div>
    </>
  );
};

export const AccountSharedInfo = () => {
  return (
    <div className="col-start-4">
      <Tooltip title="Please reach out to account owner for account information">
        {/** Added this empty wrapper seems to fix the error: https://github.com/ant-design/ant-design/issues/48709#issuecomment-2458882498 */}
        <>
          <InfoCircleIcon className="size-8 fill-feedback-info-muted" />
        </>
      </Tooltip>
    </div>
  );
};
